/**
 * ENTITLEMENT-LIFECYCLE-1 Phase 1 — the firms-by-products matrix (EL-3).
 *
 * Split out of EntitlementsPage.tsx under docs/FILE_SIZE_POLICY.md. Presentation
 * only: it renders what the server said and reports clicks upward. It never
 * predicts the outcome of a grant, because the server re-decides every call.
 */
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { FirmRow, ProductCell } from '@/components/admin/entitlementTypes'
import { fmtDate, productLabel } from '@/components/admin/entitlementTypes'

/**
 * Three states, not two. "Never held" and "held and lost" are both un-entitled,
 * and an operator looking at a churned firm needs to tell them apart without
 * opening the audit log — so a revoked cell says when.
 */
export function CellState({ cell }: { cell: ProductCell }) {
  if (cell.entitled) {
    return (
      <div>
        <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
          Entitled
        </span>
        {fmtDate(cell.granted_at) && (
          <p className="mt-1 text-xs text-gray-500">since {fmtDate(cell.granted_at)}</p>
        )}
      </div>
    )
  }
  if (cell.ever_granted) {
    return (
      <div>
        <span className="inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          Revoked
        </span>
        {fmtDate(cell.revoked_at) && (
          <p className="mt-1 text-xs text-gray-500">{fmtDate(cell.revoked_at)}</p>
        )}
      </div>
    )
  }
  return <span className="text-xs text-gray-400">Not entitled</span>
}

interface EntitlementMatrixProps {
  firms: FirmRow[]
  products: string[]
  onAction: (
    action: 'grant' | 'revoke',
    accountId: string,
    product: string,
    cell: ProductCell,
  ) => void
}

const EMPTY_CELL: ProductCell = { entitled: false, ever_granted: false }

export function EntitlementMatrix({ firms, products, onAction }: EntitlementMatrixProps) {
  return (
    /* The matrix is wider than a phone. It scrolls inside its own box rather
       than pushing the page sideways — Stage 4b's rule for financial and
       wide tables applies here too. */
    <div className="overflow-x-auto rounded-lg border" data-testid="entitlements-matrix">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[10rem]">Firm</TableHead>
            {products.map(p => (
              <TableHead key={p} className="min-w-[9rem]">
                {productLabel(p)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {firms.map(firm => (
            <TableRow key={firm.account_id}>
              <TableCell className="align-top text-sm font-medium">
                {firm.account_id}
              </TableCell>
              {products.map(product => {
                const cell = firm.products[product] ?? EMPTY_CELL
                const action = cell.entitled ? 'revoke' : 'grant'
                return (
                  <TableCell key={product} className="align-top">
                    <CellState cell={cell} />
                    <Button
                      className="mt-2"
                      variant={cell.entitled ? 'outline' : 'default'}
                      size="sm"
                      data-testid={`${action}-${firm.account_id}-${product}`}
                      onClick={() => onAction(action, firm.account_id, product, cell)}
                    >
                      {cell.entitled ? 'Revoke' : 'Grant'}
                    </Button>
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
